import {
  BrowserDataCacheSqlDriver,
  BrowserDataCacheSqlDriverFactory,
  BrowserDataCacheSqlRow,
  BrowserDataCacheSqlValue,
} from './driver';

export interface BrowserDataCacheSqlJsModule {
  Database: new (bytes?: Uint8Array) => BrowserDataCacheSqlJsDatabase;
}

export interface BrowserDataCacheSqlJsDatabase {
  exec(sql: string): unknown;
  prepare(sql: string): BrowserDataCacheSqlJsStatement;
  export(): Uint8Array;
  close?(): void;
}

export interface BrowserDataCacheSqlJsStatement {
  bind(params?: BrowserDataCacheSqlValue[]): void;
  step(): boolean;
  getAsObject(): BrowserDataCacheSqlRow;
  free(): void;
}

export interface BrowserDataCacheOpfsRoot {
  getFileHandle(name: string, options?: { create?: boolean }): Promise<BrowserDataCacheOpfsFileHandle>;
  removeEntry?(name: string): Promise<void>;
}

export interface BrowserDataCacheOpfsFileHandle {
  getFile(): Promise<{ size?: number; arrayBuffer(): Promise<ArrayBuffer> }>;
  createWritable(): Promise<{
    write(data: Uint8Array): Promise<void> | void;
    close(): Promise<void> | void;
  }>;
}

export interface SqlJsOpfsDriverOptions {
  dbFileName: string;
  loadSqlJs: () => Promise<BrowserDataCacheSqlJsModule> | BrowserDataCacheSqlJsModule;
  getDirectory?: () => Promise<BrowserDataCacheOpfsRoot>;
}

export interface SqlJsOpfsProbeOptions extends SqlJsOpfsDriverOptions {
  probeFileName: string;
}

export interface SqlJsOpfsProbeResult {
  ok: boolean;
  opfsAvailable: boolean;
  sqliteAvailable: boolean;
  error?: string;
}

export class SqlJsOpfsDriver implements BrowserDataCacheSqlDriver {
  private sqlite: BrowserDataCacheSqlJsModule | null = null;
  private db: BrowserDataCacheSqlJsDatabase | null = null;
  private fileHandle: BrowserDataCacheOpfsFileHandle | null = null;

  constructor(private readonly options: SqlJsOpfsDriverOptions) {}

  async open(): Promise<void> {
    if (this.db) return;
    const root = await getOpfsRoot(this.options.getDirectory);
    this.sqlite = await this.options.loadSqlJs();
    this.fileHandle = await root.getFileHandle(this.options.dbFileName, { create: true });
    const file = await this.fileHandle.getFile();
    const bytes = new Uint8Array(await file.arrayBuffer());
    this.db = bytes.length > 0 ? new this.sqlite.Database(bytes) : new this.sqlite.Database();
  }

  async execute(sql: string, params: BrowserDataCacheSqlValue[] = []): Promise<void> {
    await this.open();
    if (!this.db) throw new Error('SQLite DB is not open.');
    if (!params.length) {
      this.db.exec(sql);
      return;
    }
    const stmt = this.db.prepare(sql);
    try {
      stmt.bind(params);
      stmt.step();
    } finally {
      stmt.free();
    }
  }

  async query<TRow extends BrowserDataCacheSqlRow = BrowserDataCacheSqlRow>(
    sql: string,
    params: BrowserDataCacheSqlValue[] = [],
  ): Promise<TRow[]> {
    await this.open();
    if (!this.db) throw new Error('SQLite DB is not open.');
    const stmt = this.db.prepare(sql);
    const rows: TRow[] = [];
    try {
      stmt.bind(params);
      while (stmt.step()) {
        rows.push(stmt.getAsObject() as TRow);
      }
    } finally {
      stmt.free();
    }
    return rows;
  }

  async transaction<T>(work: () => Promise<T> | T): Promise<T> {
    await this.execute('BEGIN;');
    try {
      const result = await work();
      await this.execute('COMMIT;');
      return result;
    } catch (error) {
      await this.execute('ROLLBACK;').catch(() => undefined);
      throw error;
    }
  }

  async flush(): Promise<void> {
    await this.open();
    if (!this.db || !this.fileHandle) return;
    const bytes = this.db.export();
    const writable = await this.fileHandle.createWritable();
    await writable.write(bytes);
    await writable.close();
  }

  async close(): Promise<void> {
    this.db?.close?.();
    this.db = null;
    this.fileHandle = null;
  }
}

export class SqlJsOpfsDriverFactory implements BrowserDataCacheSqlDriverFactory {
  constructor(private readonly options: SqlJsOpfsDriverOptions) {}

  create(): SqlJsOpfsDriver {
    return new SqlJsOpfsDriver(this.options);
  }
}

export async function probeSqlJsOpfs(options: SqlJsOpfsProbeOptions): Promise<SqlJsOpfsProbeResult> {
  let db: BrowserDataCacheSqlJsDatabase | null = null;
  try {
    const root = await getOpfsRoot(options.getDirectory);
    const sqlite = await options.loadSqlJs();
    const fileHandle = await root.getFileHandle(options.probeFileName, { create: true });
    db = new sqlite.Database();
    db.exec('CREATE TABLE IF NOT EXISTS probe (id TEXT PRIMARY KEY, value TEXT);');
    db.exec("INSERT INTO probe (id, value) VALUES ('probe', 'ok');");
    const writable = await fileHandle.createWritable();
    await writable.write(db.export());
    await writable.close();
    await root.removeEntry?.(options.probeFileName).catch(() => undefined);
    return { ok: true, opfsAvailable: true, sqliteAvailable: true };
  } catch (error: any) {
    const message = String(error?.message || error || 'OPFS/sql.js probe failed');
    const opfsAvailable = !/OPFS is unavailable/i.test(message);
    return {
      ok: false,
      opfsAvailable,
      sqliteAvailable: opfsAvailable ? false : false,
      error: message,
    };
  } finally {
    db?.close?.();
  }
}

export async function removeSqlJsOpfsFiles(
  fileNames: string[],
  getDirectory?: () => Promise<BrowserDataCacheOpfsRoot>,
): Promise<void> {
  const root = await getOpfsRoot(getDirectory);
  for (const fileName of fileNames || []) {
    if (!fileName) continue;
    await root.removeEntry?.(fileName).catch(() => undefined);
  }
}

async function getOpfsRoot(getDirectory?: () => Promise<BrowserDataCacheOpfsRoot>): Promise<BrowserDataCacheOpfsRoot> {
  if (getDirectory) {
    return getDirectory();
  }
  const storage = (globalThis as any).navigator?.storage;
  if (!storage || typeof storage.getDirectory !== 'function') {
    throw new Error('OPFS is unavailable in this browser context.');
  }
  return storage.getDirectory();
}
